import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from './SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCamera, FiLoader, FiAlertCircle, FiCheckCircle } = FiIcons;

/**
 * Capture the current browser tab via Screen Capture API and push the image
 * into the analysis queue (same shape as manual upload / paste).
 *
 * Props:
 *   - onCaptured(imageObject): called with the {id, file, preview, name, ...}
 *   - autoAnalyze (boolean): when true, also triggers analyze immediately (caller decides how)
 *   - onAnalyzeRequested(image): callback when autoAnalyze is true
 *   - targetRef: React ref pointing to the DOM element to crop the capture to (e.g. chart container).
 *                If omitted, the full tab is captured.
 *   - currentSymbol, currentTimeframe: optional, embedded into the name
 *   - disabled: greys out the button
 *   - capturedCount: shown on the button as "(N)" so the user knows how many shots are queued
 */
const CaptureChartButton = ({
  onCaptured,
  autoAnalyze = false,
  onAnalyzeRequested,
  targetRef = null,
  currentSymbol = 'XAUUSD',
  currentTimeframe = '15m',
  disabled = false,
  capturedCount = 0,
  variant = 'primary', // 'primary' (blue/cyan filled) | 'dashboard' (purple/cyan glass)
  className = '',
}) => {
  const [status, setStatus] = useState('idle'); // idle | capturing | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const isSupported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getDisplayMedia === 'function';

  const captureChart = async () => {
    if (!isSupported) {
      setStatus('error');
      setErrorMsg('Browser non supporté. Utilise Chrome, Edge ou Firefox sur desktop.');
      return;
    }

    // Resolve the crop area BEFORE prompting for screen share so the layout doesn't shift
    let cropBounds = null;
    if (targetRef && targetRef.current) {
      const rect = targetRef.current.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        cropBounds = {
          x: Math.max(0, Math.round(rect.x)),
          y: Math.max(0, Math.round(rect.y)),
          width: Math.round(rect.width),
          height: Math.round(rect.height),
        };
      }
    }

    setStatus('capturing');
    setErrorMsg('');
    let stream = null;
    try {
      // Ask user to share their current tab.
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'browser',
          // eslint-disable-next-line no-restricted-syntax
          cursor: 'never',
        },
        audio: false,
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
      });

      const track = stream.getVideoTracks()[0];

      // Read one frame to a full-tab canvas first.
      let fullCanvas;
      let width;
      let height;
      if (typeof window.ImageCapture === 'function') {
        const imageCapture = new window.ImageCapture(track);
        const bitmap = await imageCapture.grabFrame();
        width = bitmap.width;
        height = bitmap.height;
        fullCanvas = document.createElement('canvas');
        fullCanvas.width = width;
        fullCanvas.height = height;
        fullCanvas.getContext('2d').drawImage(bitmap, 0, 0);
      } else {
        const video = document.createElement('video');
        video.srcObject = stream;
        await video.play();
        await new Promise((r) => setTimeout(r, 200));
        width = video.videoWidth;
        height = video.videoHeight;
        fullCanvas = document.createElement('canvas');
        fullCanvas.width = width;
        fullCanvas.height = height;
        fullCanvas.getContext('2d').drawImage(video, 0, 0, width, height);
      }

      // Stop the screen-share immediately so the browser banner disappears
      stream.getTracks().forEach((t) => t.stop());
      stream = null;

      // Crop to the chart container if we have its bounds.
      // The capture frame is in PHYSICAL pixels while getBoundingClientRect is in CSS pixels,
      // so we scale by the ratio between capture width and viewport width.
      let blob;
      if (cropBounds) {
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const scaleX = width / viewportWidth;
        const scaleY = height / viewportHeight;
        const sx = Math.max(0, Math.floor(cropBounds.x * scaleX));
        const sy = Math.max(0, Math.floor(cropBounds.y * scaleY));
        const sw = Math.min(width - sx, Math.ceil(cropBounds.width * scaleX));
        const sh = Math.min(height - sy, Math.ceil(cropBounds.height * scaleY));
        const cropCanvas = document.createElement('canvas');
        cropCanvas.width = sw;
        cropCanvas.height = sh;
        cropCanvas.getContext('2d').drawImage(fullCanvas, sx, sy, sw, sh, 0, 0, sw, sh);
        blob = await new Promise((res) => cropCanvas.toBlob(res, 'image/png', 0.95));
        width = sw;
        height = sh;
      } else {
        blob = await new Promise((res) => fullCanvas.toBlob(res, 'image/png', 0.95));
      }

      // Build the same image shape that handleImageUpload / handlePaste produce
      const preview = await blobToDataUrl(blob);
      const file = new File(
        [blob],
        `auto-capture-${currentSymbol}-${currentTimeframe}-${Date.now()}.png`,
        { type: 'image/png' }
      );

      const imageObject = {
        id: Date.now() + Math.random(),
        file,
        preview,
        name: `📸 ${currentSymbol} ${currentTimeframe} (auto-capture)`,
        size: blob.size,
        width,
        height,
        containsPrice: true,
        captureMethod: 'screen-capture',
        symbol: currentSymbol,
        timeframe: currentTimeframe,
        uploadTime: new Date().toLocaleString(),
        timestamp: new Date().toISOString(),
      };

      if (typeof onCaptured === 'function') {
        onCaptured(imageObject);
      }
      if (autoAnalyze && typeof onAnalyzeRequested === 'function') {
        // Defer slightly so React state has time to commit the new image
        setTimeout(() => onAnalyzeRequested(imageObject), 100);
      }

      setStatus('success');
      // Reset visual state after a moment so user can click again
      setTimeout(() => setStatus('idle'), 2500);
    } catch (err) {
      // User clicked Cancel on the share dialog => NotAllowedError or AbortError
      if (stream) stream.getTracks().forEach((t) => t.stop());
      const message = err && err.message ? err.message : String(err);
      if (err && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
        setStatus('idle');
        return;
      }
      console.error('CaptureChartButton error:', err);
      setStatus('error');
      setErrorMsg(message);
      setTimeout(() => setStatus('idle'), 4000);
    }
  };

  // Two visual variants:
  //   - 'primary'   : solid blue/cyan filled (default, stands out as a CTA)
  //   - 'dashboard' : purple/cyan translucent with border (matches the chart toolbar look)
  const sizing = variant === 'dashboard'
    ? 'px-3 py-1.5 text-xs rounded-xl'
    : 'px-4 py-2.5 text-sm rounded-lg';
  const baseClasses = `relative group inline-flex items-center gap-2 font-semibold transition-all duration-300 ${sizing}`;
  const themeClasses = variant === 'dashboard'
    ? {
        idle:      'bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 text-cyan-400 shadow-lg shadow-purple-500/20',
        capturing: 'bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/20 text-gray-400 cursor-wait',
        success:   'bg-gradient-to-r from-emerald-600/30 to-emerald-500/30 border border-emerald-500/50 text-emerald-300',
        error:     'bg-gradient-to-r from-rose-600/30 to-rose-500/30 border border-rose-500/50 text-rose-300',
      }[status]
    : {
        idle:      'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white shadow-lg shadow-cyan-500/20',
        capturing: 'bg-gray-700 text-gray-300 cursor-wait',
        success:   'bg-emerald-600 text-white',
        error:     'bg-rose-600 text-white',
      }[status];

  const idleLabel = autoAnalyze
    ? 'Capture & Analyze'
    : capturedCount > 0
      ? `Capture chart (${capturedCount} queued)`
      : 'Capture chart';
  const label = {
    idle: idleLabel,
    capturing: 'Capturing…',
    success: capturedCount + 1 > 0 ? `Captured ✓ (${capturedCount + 1} queued)` : 'Captured ✓',
    error: 'Failed',
  }[status];

  const Icon = {
    idle: FiCamera,
    capturing: FiLoader,
    success: FiCheckCircle,
    error: FiAlertCircle,
  }[status];

  return (
    <div className={`flex flex-col items-start ${className}`}>
      <motion.button
        type="button"
        onClick={captureChart}
        disabled={disabled || status === 'capturing'}
        whileHover={{ scale: disabled ? 1 : 1.02 }}
        whileTap={{ scale: disabled ? 1 : 0.98 }}
        className={`${baseClasses} ${themeClasses} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        title={isSupported ? 'Take a screenshot of the current chart and send to AI' : 'Screen capture not supported in this browser'}
      >
        <SafeIcon
          icon={Icon}
          className={`${variant === 'dashboard' ? 'w-3.5 h-3.5' : 'w-4 h-4'} ${status === 'capturing' ? 'animate-spin' : ''}`}
        />
        <span className={variant === 'dashboard' ? 'hidden sm:inline' : ''}>{label}</span>
        {variant === 'dashboard' && capturedCount > 0 && status === 'idle' && (
          <span className="text-[9px] bg-purple-500/30 text-cyan-300 px-1.5 py-0.5 rounded font-mono">{capturedCount}</span>
        )}
      </motion.button>
      {status === 'error' && errorMsg && (
        <p className="text-xs text-rose-400 mt-1 max-w-xs">{errorMsg}</p>
      )}
      {!isSupported && status !== 'error' && (
        <p className="text-xs text-amber-400 mt-1">
          Capture indisponible sur ce navigateur (Chrome/Edge/Firefox desktop requis).
        </p>
      )}
    </div>
  );
};

async function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export default CaptureChartButton;
